import styles from './active-users-grid.module.scss';
import { booleanHeader, CreateHeaders, dateAndTimeHeader, HeaderSizeEnum, minuteToHourConverter, PrimaryButton, textHeader } from '@frontend-workspace/react-shared-lib';
import { ValueFormatterParams } from 'ag-grid-community';
import { useState } from 'react';
import { ActiveUserDetailsCellRenderer } from '../active-users-grid-cell-renderer/active-users-grid-cell-renderer';
import { IActiveUser } from '../../_models/active-user';
import { useTableConfigHook } from '../../requestUtils';
import WebkuaTable from '../../webkua-table/webkua-table';
import ReleaseSessionDialog from '../../release-session-dialog/release-session-dialog';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface ActiveUsersGridProps {
  activeUsers: IActiveUser[],
  getData: () => void,
  isGroup: boolean,
  isLoading: boolean,
  onIsGroupChange: () => void
}

export function ActiveUsersGrid(props: ActiveUsersGridProps) {
  const { t } = useTranslation();
  const [session, setSession] = useState<IActiveUser | undefined>(undefined);
  const [onlyActiveUsers, setOnlyActiveUsers] = useState<boolean>(true);
  const [onlyMainProducts, setOnlyMainProducts] = useState<boolean>(false);
  const tableConfig = useTableConfigHook("activeUsers");

  const disconnectedMinutesRenderer = (params: ValueFormatterParams) => minuteToHourConverter(params.value, t("activeUsersGrid.connected"));

  const dialogButtonRenderer = (params: ValueFormatterParams) => {
    const onClick = () => setSession(params.data);
    return <PrimaryButton
      variant="outlined"
      size='small'
      onClick={onClick}>
      {t("standard.freeUpLoans")}
    </PrimaryButton>
  };

  const filteredActiveUsers = props.activeUsers
    .filter(au => onlyActiveUsers ? au.isActive : true)
    .filter(au => onlyMainProducts ? !au.isModule : true);

  const headers = CreateHeaders([
    textHeader('email', t("standard.email"), HeaderSizeEnum.EXTRA_LARGE, {
      renderer: 'agGroupCellRenderer',
      cellStyle: { fontSize: '0.875rem', fontWeight: '600' }
    }),
    textHeader("personName", t("standard.person"), HeaderSizeEnum.EXTRA_LARGE),
    textHeader("companyName", t("standard.companyName"), HeaderSizeEnum.EXTRA_LARGE),
    textHeader("companyNumber", t("standard.companyNumber"), HeaderSizeEnum.MEDIUM),
    textHeader("productName", t("standard.product"), HeaderSizeEnum.EXTRA_LARGE),
    textHeader("version", t("activeUsersGrid.version"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),

    textHeader("productVersion", "Programversjon", HeaderSizeEnum.MEDIUM),
    textHeader("licenseModel", t("standard.licenseModel"), HeaderSizeEnum.LARGE),
    textHeader("licenseNumber", t("activeUsersGrid.licenseNo"), HeaderSizeEnum.MEDIUM),
    textHeader("disconnectedMinutes", t("activeUsersGrid.offline"), HeaderSizeEnum.MEDIUM, {
      renderer: disconnectedMinutesRenderer
    }),
    textHeader("maxSeats", t("activeUsersGrid.maxSeats"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("seatNumber", t("activeUsersGrid.loggedInNow"), HeaderSizeEnum.LARGE, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    booleanHeader("isModule", t("standard.module"), { hide: !onlyMainProducts }),
    booleanHeader("isActive", t("standard.active"), { hide: onlyActiveUsers }),
    dateAndTimeHeader("expiration", t("standard.expiryDate"), { hide: true }),
    dateAndTimeHeader("loanCreated", t("standard.loanStart"), { hide: true }),
    booleanHeader("isNewLicensing", t("standard.newLicensingSystem")),
    textHeader("", "", HeaderSizeEnum.LARGE, {
      renderer: dialogButtonRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]);

  return <>
    <WebkuaTable
      onRefresh={props.getData}
      isLoading={props.isLoading}
      headers={headers}
      tableConfig={tableConfig}
      pageSize={15}
      expandComponent={ActiveUserDetailsCellRenderer}
      data={filteredActiveUsers}
      tableFilters={[{
        label: t("standard.showForGroup"),
        checked: props.isGroup,
        onChange: props.onIsGroupChange
      }, {
        label: t("activeUsersGrid.onlyActiveUsers"),
        checked: onlyActiveUsers,
        onChange: setOnlyActiveUsers
      }, {
        label: t("activeUsersGrid.mainProductsOnly"),
        checked: onlyMainProducts,
        onChange: setOnlyMainProducts
      }]}
    />
    {session &&
      <ReleaseSessionDialog
        onClose={() => setSession(undefined)}
        selectedSession={session}
        getData={props.getData}
      />}
  </>;
}

export default ActiveUsersGrid;